import { STORAGE_KEYS } from '@/constants/storage-keys';

export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];

export const TEXT_SIZES = ['standard', 'large', 'x-large'] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

export interface Preferences {
  theme: Theme;
  textSize: TextSize;
}

/** Light by default — the client prefers it. Dark stays one click away in Settings. */
const DEFAULTS: Preferences = { theme: 'light', textSize: 'standard' };

const DARK_QUERY = '(prefers-color-scheme: dark)';

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return allowed.find((option) => option === value) ?? fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Not persisted — the choice still applies until the page reloads.
  }
}

let current: Preferences = {
  theme: readStored(STORAGE_KEYS.THEME, THEMES, DEFAULTS.theme),
  textSize: readStored(STORAGE_KEYS.TEXT_SIZE, TEXT_SIZES, DEFAULTS.textSize),
};
const listeners = new Set<() => void>();

function resolvedTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/** The phone browser's bar takes the app's ground colour, read from tokens.css. */
function syncThemeColor(root: HTMLElement): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  const ground = getComputedStyle(root).getPropertyValue('--color-ground').trim();
  if (meta && ground) meta.content = ground;
}

/** Writes the preferences onto <html>, where tokens.css and global.css read them. */
function apply(): void {
  const root = document.documentElement;
  root.dataset.theme = resolvedTheme(current.theme);
  if (current.textSize === 'standard') delete root.dataset.textSize;
  else root.dataset.textSize = current.textSize;
  syncThemeColor(root);
}

function update(next: Partial<Preferences>): void {
  current = { ...current, ...next };
  apply();
  for (const listener of listeners) listener();
}

/**
 * Per-device display preferences (theme, text size), kept in localStorage.
 * `initPreferences()` runs once before the first render so the page never flashes the wrong theme.
 */
export const preferencesStore = {
  get: (): Preferences => current,
  setTheme: (theme: Theme): void => {
    store(STORAGE_KEYS.THEME, theme);
    update({ theme });
  },
  setTextSize: (textSize: TextSize): void => {
    store(STORAGE_KEYS.TEXT_SIZE, textSize);
    update({ textSize });
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function initPreferences(): void {
  apply();
  // "Match device" follows the OS when it switches between light and dark.
  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (current.theme === 'system') apply();
  });
}
